import {
  backButton,
  init as initSDK,
  initData,
  isTMA,
  mainButton,
  miniApp,
  retrieveLaunchParams,
  setDebug,
  themeParams,
} from '@telegram-apps/sdk-react'
import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { applyColorScheme, mountTheme, mountViewport } from './theme'

export type TelegramStatus = 'starting' | 'ready' | 'outside' | 'failed'

export interface TelegramSession {
  initDataRaw: string | undefined
  firstName: string | undefined
  languageCode: string | undefined
  platform: string | undefined
  startParam: string | undefined
  degraded: string[]
}

const TelegramContext = createContext<TelegramSession | undefined>(undefined)

// How long any one optional step gets. Every step here asks the Telegram client
// a question, and a client that never answers is not a failure any of them
// report — the promise simply never settles. Without a ceiling the app sits on
// «Loading…» for ever, which is the least diagnosable state it has.
const STEP_TIMEOUT_MS = 3000

function reasonOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function within<T>(ms: number, work: Promise<T>): Promise<T> {
  return Promise.race([
    work,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`gave up after ${ms}ms`)), ms),
    ),
  ])
}

async function bootstrap(debug: boolean): Promise<TelegramSession> {
  const degraded: string[] = []

  const soft = async (what: string, step: () => unknown): Promise<void> => {
    try {
      await within(STEP_TIMEOUT_MS, Promise.resolve().then(step))
    } catch (error) {
      degraded.push(`${what}: ${reasonOf(error)}`)
    }
  }

  initSDK()

  await soft('debug', () => setDebug(debug))
  await soft('init data', () => initData.restore())
  await soft('theme', mountTheme)
  await soft('back button', () => backButton.mount.ifAvailable())
  await soft('main button', () => mainButton.mount.ifAvailable())
  await soft('viewport', mountViewport)
  await soft('colour scheme', () => applyColorScheme(isDark()))
  await soft('ready', () => miniApp.ready.ifAvailable())

  const raw = initData.raw()

  if (!raw) {
    throw new Error('Telegram sent no init data, so there is nobody to sign requests as')
  }

  let platform: string | undefined

  await soft('launch parameters', () => {
    platform = retrieveLaunchParams(true).tgWebAppPlatform
  })

  return {
    initDataRaw: raw,
    firstName: initData.user()?.first_name,
    languageCode: initData.user()?.language_code,
    platform,
    startParam: initData.startParam(),
    degraded,
  }
}

function isDark(): boolean {
  try {
    return miniApp.isDark()
  } catch {
    try {
      return themeParams.isDark()
    } catch {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
  }
}

export function TelegramProvider({
  children,
  fallback,
}: {
  children: (session: TelegramSession) => ReactNode
  fallback: (status: Exclude<TelegramStatus, 'ready'>, reason?: string) => ReactNode
}) {
  const [inTelegram] = useState(() => isTMA())
  const [status, setStatus] = useState<TelegramStatus>(inTelegram ? 'starting' : 'outside')
  const [session, setSession] = useState<TelegramSession>()
  const [reason, setReason] = useState<string>()

  useEffect(() => {
    if (!inTelegram) {
      return
    }

    let cancelled = false

    bootstrap(import.meta.env.DEV)
      .then((opened) => {
        if (cancelled) {
          return
        }

        setSession(opened)
        setStatus('ready')
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setReason(reasonOf(error))
          setStatus('failed')
        }
      })

    return () => {
      cancelled = true
    }
  }, [inTelegram])

  if (status === 'ready' && session) {
    return <TelegramContext value={session}>{children(session)}</TelegramContext>
  }

  return fallback(status === 'ready' ? 'starting' : status, reason)
}

export function useFirstName(): string | undefined {
  return useContext(TelegramContext)?.firstName
}

export function useTelegram(): TelegramSession {
  const session = useContext(TelegramContext)

  if (!session) {
    throw new Error('useTelegram is only usable inside a TelegramProvider')
  }

  return session
}
